import {
    Address,
    BeneficialOwner,
    BusinessContact,
    Officer,
    Phone,
    UnitErrorPayload,
    UnitResponse,
} from "@unit-finance/unit-node-sdk";

interface IDeviceFingerprint {
    provider: "iovation";
    value: string;
}

interface ITags {
    promo_code?: string;
}

// https://docs.unit.co/applications#create-business-application
export interface UnitApplicationRequest {
    name: string;
    dba: string;
    address: Address;
    phone: Phone;
    stateOfIncorporation: string;
    ein: string;
    entityType: string;
    website: string;
    contact: BusinessContact;
    officer: Officer;
    beneficialOwners: BeneficialOwner[];
    yearOfIncorporation: string;
    businessVertical: BusinessVertical;
    annualRevenue: AnnualRevenue;
    numberOfEmployees: NumberOfEmployees;
    cashFlow: CashFlow;
    countriesOfOperation: string[];
    idempotencyKey: string;
    deviceFingerprints: IDeviceFingerprint[] | undefined;
    tags?: ITags;
}

export interface UnitResponseWithError<T> extends UnitResponse<T> {
    errors?: UnitErrorPayload[];
}

// TODO(@kteramoto): Import from @unit-finance/unit-node-sdk when available
// https://docs.unit.co/types/#business-vertical
export type BusinessVertical =
    | "AdultEntertainmentDatingOrEscortServices"
    | "AgricultureForestryFishingOrHunting"
    | "ArtsEntertainmentAndRecreation"
    | "BusinessSupportOrBuildingServices"
    | "Cannabis"
    | "Construction"
    | "DirectMarketingOrTelemarketing"
    | "EducationalServices"
    | "FinancialServicesCryptocurrency"
    | "FinancialServicesDebitCollectionOrConsolidation"
    | "FinancialServicesMoneyServicesBusinessOrCurrencyExchange"
    | "FinancialServicesOther"
    | "FinancialServicesPaydayLending"
    | "GamingOrGambling"
    | "HealthCareAndSocialAssistance"
    | "HospitalityAccommodationOrFoodServices"
    | "LegalAccountingConsultingOrComputerProgramming"
    | "Manufacturing"
    | "Mining"
    | "Nutraceuticals"
    | "PersonalCareServices"
    | "PublicAdministration"
    | "RealEstate"
    | "ReligiousCivicAndSocialOrganizations"
    | "RepairAndMaintenance"
    | "RetailTrade"
    | "TechnologyMediaOrTelecom"
    | "TransportationOrWarehousing"
    | "Utilities";

export const BUSINESS_VERTICAL_DISPLAY_NAMES: Record<BusinessVertical, string> = {
    AdultEntertainmentDatingOrEscortServices: "Adult Entertainment, Dating or Escort Services",
    AgricultureForestryFishingOrHunting: "Agriculture, Forestry, Fishing or Hunting",
    ArtsEntertainmentAndRecreation: "Arts, Entertainment and Recreation",
    BusinessSupportOrBuildingServices: "Business Support or Building Services",
    Cannabis: "Cannabis",
    Construction: "Construction",
    DirectMarketingOrTelemarketing: "Direct Marketing or Telemarketing",
    EducationalServices: "Educational Services",
    FinancialServicesCryptocurrency: "Financial Services - Cryptocurrency",
    FinancialServicesDebitCollectionOrConsolidation: "Financial Services - Debit Collection or Consolidation",
    FinancialServicesMoneyServicesBusinessOrCurrencyExchange:
        "Financial Services - Money Services Business or Currency Exchange",
    FinancialServicesPaydayLending: "Financial Services - Payday Lending",
    FinancialServicesOther: "Financial Services - Other",
    GamingOrGambling: "Gaming or Gambling",
    HealthCareAndSocialAssistance: "Health Care and Social Assistance",
    HospitalityAccommodationOrFoodServices: "Hospitality, Accommodation or Food Services",
    LegalAccountingConsultingOrComputerProgramming: "Legal, Accounting, Consulting or Computer Programming",
    Manufacturing: "Manufacturing",
    Mining: "Mining",
    Nutraceuticals: "Nutraceuticals",
    PersonalCareServices: "Personal Care Services",
    PublicAdministration: "Public Administration",
    RealEstate: "Real Estate",
    ReligiousCivicAndSocialOrganizations: "Religious, Civic and Social Organizations",
    RepairAndMaintenance: "Repair and Maintenance",
    RetailTrade: "Retail Trade",
    TechnologyMediaOrTelecom: "Technology, Media or Telecom",
    TransportationOrWarehousing: "Transportation or Warehousing",
    Utilities: "Utilities",
};

// https://docs.unit.co/types/#occupation
export type Occupation =
    | "ArchitectOrEngineer"
    | "BusinessAnalystAccountantOrFinancialAdvisor"
    | "CommunityAndSocialServicesWorker"
    | "ConstructionMechanicOrMaintenanceWorker"
    | "Doctor"
    | "Educator"
    | "EntertainmentSportsArtsOrMedia"
    | "ExecutiveOrManager"
    | "FarmerFishermanForester"
    | "FoodServiceWorker"
    | "GigWorker"
    | "HospitalityOfficeOrAdministrativeSupportWorker"
    | "HouseholdManager"
    | "JanitorHousekeeperLandscaper"
    | "Lawyer"
    | "ManufacturingOrProductionWorker"
    | "MilitaryOrPublicSafety"
    | "NurseHealthcareTechnicianOrHealthcareSupport"
    | "PersonalCareOrServiceWorker"
    | "PilotDriverOperator"
    | "SalesRepresentativeBrokerAgent"
    | "ScientistOrTechnologist"
    | "Student";

export const OCCUPATION_DISPLAY_NAMES: Record<Occupation, string> = {
    ArchitectOrEngineer: "Architect or Engineer",
    BusinessAnalystAccountantOrFinancialAdvisor: "Business Analyst, Accountant or Financial Advisor",
    CommunityAndSocialServicesWorker: "Community and Social Services Worker",
    ConstructionMechanicOrMaintenanceWorker: "Construction, Mechanic or Maintenance Worker",
    Doctor: "Doctor",
    Educator: "Educator",
    EntertainmentSportsArtsOrMedia: "Entertainment, Sports, Arts or Media",
    ExecutiveOrManager: "Executive or Manager",
    FarmerFishermanForester: "Farmer, Fisherman, Forester",
    FoodServiceWorker: "Food Service Worker",
    GigWorker: "Gig Worker",
    HospitalityOfficeOrAdministrativeSupportWorker: "Hospitality, Office or Administrative Support Worker",
    HouseholdManager: "Household Manager",
    JanitorHousekeeperLandscaper: "Janitor, Housekeeper, Landscaper",
    Lawyer: "Lawyer",
    ManufacturingOrProductionWorker: "Manufacturing or Production Worker",
    MilitaryOrPublicSafety: "Military or Public Safety",
    NurseHealthcareTechnicianOrHealthcareSupport: "Nurse, Healthcare Technician or Healthcare Support",
    PersonalCareOrServiceWorker: "Personal Care or Service Worker",
    PilotDriverOperator: "Pilot, Driver, Operator",
    SalesRepresentativeBrokerAgent: "Sales Representative, Broker, Agent",
    ScientistOrTechnologist: "Scientist or Technologist",
    Student: "Student",
};

// https://docs.unit.co/types/#source-of-income
export type SourceOfIncome =
    | "EmploymentOrPayrollIncome"
    | "PartTimeOrContractorIncome"
    | "InheritancesAndGifts"
    | "PersonalInvestments"
    | "BusinessOwnershipInterests"
    | "GovernmentBenefits";

export const SOURCE_OF_INCOME_DISPLAY_NAMES: Record<SourceOfIncome, string> = {
    EmploymentOrPayrollIncome: "Employment or payroll income",
    PartTimeOrContractorIncome: "Part-time or contractor income",
    InheritancesAndGifts: "Inheritances and/or gifts from friends and family",
    PersonalInvestments: "Personal investments",
    BusinessOwnershipInterests: "Business ownership interests",
    GovernmentBenefits: "Government benefits",
};

// https://docs.unit.co/types/#annual-income
export type AnnualIncome =
    | "UpTo10k"
    | "Between10kAnd25k"
    | "Between25kAnd50k"
    | "Between50kAnd100k"
    | "Between100kAnd250k"
    | "Over250k";

export const ANNUAL_INCOME_DISPLAY_NAMES: Record<AnnualIncome, string> = {
    UpTo10k: "$0 - $10,000",
    Between10kAnd25k: "$10,000 - $25,000",
    Between25kAnd50k: "$25,000 - $50,000",
    Between50kAnd100k: "$50,000 - $100,000",
    Between100kAnd250k: "$100,000 - $250,000",
    Over250k: "$250,000+",
};

// https://docs.unit.co/types/#annual-revenue
export type AnnualRevenue = "UpTo250k" | "Between250kAnd500k" | "Between500kAnd1m" | "Between1mAnd5m" | "Over5m";

export const ANNUAL_REVENUE_DISPLAY_NAMES: Record<AnnualRevenue, string> = {
    UpTo250k: "$0 - $250,000",
    Between250kAnd500k: "$250,000 - $500,000",
    Between500kAnd1m: "$500,000 - $1 million",
    Between1mAnd5m: "$1 million - $5 million",
    Over5m: "$5+ million",
};

// https://docs.unit.co/types/#number-of-employees
export type NumberOfEmployees = "UpTo10" | "Between10And50" | "Between50And100" | "Between100And500" | "Over500";

export const NUMBER_OF_EMPLOYEES_DISPLAY_NAMES: Record<NumberOfEmployees, string> = {
    UpTo10: "0 - 10",
    Between10And50: "10 - 50",
    Between50And100: "50 - 100",
    Between100And500: "100 - 500",
    Over500: "500+",
};

// https://docs.unit.co/types/#cash-flow
export type CashFlow = "Unpredictable" | "Predictable";

export const CASH_FLOW_DISPLAY_NAMES: Record<CashFlow, string> = {
    Unpredictable: "Unpredictable - I'm a new company / my cash flow fluctuates",
    Predictable: "Predictable - I'm an established company with predictable cash flows",
};
